/**
 *  @class
 *  @function QuickInfo
 *  Renders stock status and expected delivery for the selected variant (quick_info block).
 *  Uses the same stock data and rules as the cart through window.DeliveryTime.
 */
if (!customElements.get('quick-info')) {
  // Below this many units the exact Viborg count is shown instead of a generic "in stock".
  const EXACT_COUNT_LIMIT = 20;
  const COUNT_PLACEHOLDER = '__COUNT__';

  const escapeHtml = (text) => {
    const element = document.createElement('div');
    element.textContent = text;
    return element.innerHTML;
  };

  const withCount = (text, count) => text.replace(COUNT_PLACEHOLDER, count);

  class QuickInfo extends HTMLElement {
    connectedCallback() {
      this.target = this.querySelector('.aqi-target');
      this.variantData = DeliveryTime.readVariantData(this);
      this.strings = JSON.parse(this.querySelector('[data-quick-info-strings]').textContent);
      this.threshold = Number(this.dataset.threshold);
      this.inventoryBase = Number(this.dataset.inventoryBase);
      this.showPulse = this.dataset.showPulse === 'true';
      this.showBar = this.dataset.showBar === 'true';

      this.onVariantChange = (event) => {
        if (event.detail.sectionId !== this.dataset.section) return;
        this.render(event.detail.variant ? event.detail.variant.id : null);
      };
      document.addEventListener('product:variant-change', this.onVariantChange);

      this.render(this.dataset.variantId);
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this.onVariantChange);
    }

    render(variantId) {
      const variant = this.variantData[variantId];
      this.target.innerHTML = variant ? this.buildMarkup(variant) : '';
    }

    buildMarkup(variant) {
      const { localStock, remoteStock, inventoryQuantity, remoteDeliveryTime } = variant;
      const strings = this.strings;

      switch (DeliveryTime.getStockStatus(variant)) {
        case 'shopify_stock': {
          const isLow = inventoryQuantity <= this.threshold;
          return this.buildNotice(
            isLow ? 'low' : 'normal',
            this.getScale(inventoryQuantity),
            isLow ? withCount(strings.onlyXLeft, inventoryQuantity) : strings.inStock
          ) + this.buildDeliveryList([{ label: strings.expectedDelivery, value: remoteDeliveryTime }]);
        }
        case 'local': {
          const isTrulyLow = localStock <= this.threshold && remoteStock === 0;
          let text = strings.inStockLocal;
          if (isTrulyLow) {
            text = withCount(strings.onlyXLeft, localStock);
          } else if (localStock < EXACT_COUNT_LIMIT) {
            text = withCount(strings.inStockLocalCount, localStock);
          }

          const rows = [{ label: strings.expectedDelivery, value: DeliveryTime.strings().localLabel }];
          if (remoteStock > 0) {
            rows.push({
              label: localStock < EXACT_COUNT_LIMIT ? withCount(strings.buyMoreThan, localStock) : strings.largerQuantities,
              value: remoteDeliveryTime,
              isSecondary: true
            });
          }
          return this.buildNotice(isTrulyLow ? 'low' : 'normal', this.getScale(localStock), text) + this.buildDeliveryList(rows);
        }
        case 'remote':
          return this.buildNotice('normal', 1, strings.onCentralWarehouse)
            + this.buildDeliveryList([{ label: strings.expectedDelivery, value: remoteDeliveryTime }]);
        case 'preorder':
          return this.buildNotice('preorder', 0, remoteDeliveryTime || strings.preorderContact);
        default:
          return '';
      }
    }

    getScale(quantity) {
      return Math.min(Math.max(quantity / this.inventoryBase, 0), 1);
    }

    buildNotice(level, scale, text) {
      return `
        <div class="product-inventory-notice product-inventory-notice--${level}" style="--inventory-scale:${scale}">
          <div class="product-inventory-notice--text">
            ${this.showPulse ? '<div class="product-inventory-notice--pulse"></div>' : ''}
            <span>${escapeHtml(text)}</span>
          </div>
          ${this.showBar ? '<div class="product-inventory-notice--bar"><span></span></div>' : ''}
        </div>`;
    }

    buildDeliveryList(rows) {
      const items = rows.map((row) => `
        <li${row.isSecondary ? ' class="secondary-delivery"' : ''}>
          <span class="delivery-label">${escapeHtml(row.label)}</span>
          <span class="delivery-value">${escapeHtml(row.value)}</span>
        </li>`).join('');
      return `<ul class="altermed-delivery-list">${items}</ul>`;
    }
  }
  customElements.define('quick-info', QuickInfo);
}
